import { useCallback, useEffect, useState } from 'react'

interface UseFullscreenResult {
  isFullscreen: boolean
  toggle: () => Promise<void>
  supported: boolean
}

/**
 * Manage fullscreen state for a target element using the browser Fullscreen API,
 * with a CSS-fixed-position fallback when the API is unavailable.
 *
 * In the fallback path, callers must apply a class (e.g., `is-fullscreen-fallback`)
 * to the target element when `isFullscreen` is true. Escape-key dismissal is wired
 * up internally for the fallback path; the native API handles Escape itself.
 *
 * `toggle()` swallows rejections from `requestFullscreen`/`exitFullscreen` (e.g.,
 * missing user gesture, denied permission, detached element) and logs a warning.
 * Callers don't need to wrap calls in try/catch.
 */
export function useFullscreen(
  targetRef: React.RefObject<HTMLElement>,
): UseFullscreenResult {
  const supported =
    typeof document !== 'undefined' && Boolean(document.fullscreenEnabled)

  const [isFullscreen, setIsFullscreen] = useState(false)

  // Sync from the native Fullscreen API.
  useEffect(() => {
    if (!supported) return
    const onChange = () => {
      setIsFullscreen(document.fullscreenElement === targetRef.current)
    }
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [supported, targetRef])

  // Fallback path: Escape-key listener only attached when fallback is active.
  useEffect(() => {
    if (supported || !isFullscreen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [supported, isFullscreen])

  // Cleanup: exit native fullscreen on unmount if we own it.
  useEffect(() => {
    return () => {
      if (supported && document.fullscreenElement === targetRef.current) {
        document.exitFullscreen().catch(() => {})
      }
    }
  }, [supported, targetRef])

  const toggle = useCallback(async () => {
    const el = targetRef.current
    if (!el) return
    if (supported) {
      try {
        if (document.fullscreenElement === el) {
          await document.exitFullscreen()
        } else {
          if (typeof el.requestFullscreen !== 'function') return
          await el.requestFullscreen()
        }
      } catch (err) {
        console.warn('[useFullscreen] toggle failed:', err)
      }
    } else {
      setIsFullscreen(v => !v)
    }
  }, [supported, targetRef])

  return { isFullscreen, toggle, supported }
}
